import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { ClickoutResult, ProgramClickoutData } from "./ClickoutResult";
import { HostedResult, HostedResultProps } from "./HostedResult";
import { NoMatchResult, NoMatchResultProps } from "./NoMatchResult";
import { ProgramUnavailableResult, ProgramUnavailableResultProps } from "./ProgramUnavailableResult";
import type {
  HostedFlowConfig,
  PayloadFormQuestion,
  PayloadProgramHighlight,
  PayloadResultScenarios,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { MultiResultChooser, type MultiResultScenario } from "./MultiResultChooser";
import type { FunnelProgressUpdate } from "../utils/funnelProgress";

export type MatchScenario =
  | { type: "CLICKOUT"; program: ProgramClickoutData; staticProgramHighlights?: PayloadProgramHighlight[]; prePingResult?: unknown }
  | { type: "HOSTED"; program: HostedResultProps["program"]; hostedFlowConfig?: HostedFlowConfig; funnelQuestions?: PayloadFormQuestion[]; staticProgramHighlights?: PayloadProgramHighlight[]; prePingResult?: unknown; searchUrl?: string }
  | MultiResultScenario
  | { type: "NO_MATCH"; searchUrl?: string; similarPrograms?: NoMatchResultProps["similarPrograms"] }
  | { type: "PROGRAM_UNAVAILABLE"; programName?: string; searchUrl?: string; similarPrograms?: ProgramUnavailableResultProps["similarPrograms"] };

export interface HostedCompletionContext {
  program: HostedResultProps["program"];
  hostedFlowConfig?: HostedFlowConfig;
}

interface ResultScenarioRouterProps {
  scenario: MatchScenario;
  clickoutConfig?: PayloadResultScenarios["clickoutConfig"];
  sessionId?: string;
  onHostedComplete?: (
    redirectUrl?: string,
    firstName?: string,
    context?: HostedCompletionContext
  ) => void;
  /** When true, displays preview badge and disables lead submission */
  isPreview?: boolean;
  /** Form funnel ID, threaded to ConfirmStep for lead submission */
  funnelId?: string;
  /** Per-tab form funnel attempt ID, threaded to final submit for enrollment linking */
  formFunnelSessionId?: string;
  /** Collected form answers, threaded to ConfirmStep for lead submission */
  answers?: Record<string, unknown>;
  /** Reports the rendered result or hosted presentation stage. */
  onProgressChange?: (progress: FunnelProgressUpdate) => void;
  /** Returns a single hosted result to the last visible funnel question. */
  onBackFromFirstHostedStep?: () => void;
}

/** Preview mode indicator displayed on the result screen */
const PreviewResultBadge = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      mb: 3,
      p: 2,
      bgcolor: "warning.light",
      borderRadius: 1,
    }}
  >
    <Chip
      label="Preview Mode"
      color="warning"
      size="small"
      sx={{ fontWeight: "bold" }}
    />
    <Typography variant="body2" color="text.secondary" textAlign="center">
      This is a preview. Lead submission is disabled.
    </Typography>
  </Box>
);

export const ResultScenarioRouter = ({
  scenario,
  clickoutConfig,
  sessionId,
  onHostedComplete,
  isPreview = false,
  funnelId,
  formFunnelSessionId,
  answers,
  onProgressChange,
  onBackFromFirstHostedStep,
}: ResultScenarioRouterProps) => {
  const renderResult = () => {
    switch (scenario.type) {
      case "CLICKOUT":
        return <ClickoutResult program={scenario.program} clickoutConfig={clickoutConfig} sessionId={sessionId} isPreview={isPreview} staticProgramHighlights={scenario.staticProgramHighlights} funnelId={funnelId} formFunnelSessionId={formFunnelSessionId} answers={answers} prePingResult={scenario.prePingResult} />;
      case "HOSTED":
        return (
          <HostedResult
            program={scenario.program}
            hostedFlowConfig={scenario.hostedFlowConfig}
            funnelQuestions={scenario.funnelQuestions}
            staticProgramHighlights={scenario.staticProgramHighlights}
            onComplete={(redirectUrl, firstName) =>
              onHostedComplete?.(redirectUrl, firstName, {
                program: scenario.program,
                hostedFlowConfig: scenario.hostedFlowConfig,
              })
            }
            isPreview={isPreview}
            funnelId={funnelId}
            formFunnelSessionId={formFunnelSessionId}
            answers={answers}
            searchUrl={scenario.searchUrl}
            onProgressChange={onProgressChange}
            onBackFromFirstStep={onBackFromFirstHostedStep}
            prePingResult={scenario.prePingResult}
          />
        );
      case "MULTI_RESULT":
        return (
          <MultiResultChooser
            scenario={scenario}
            clickoutConfig={clickoutConfig}
            sessionId={sessionId}
            onHostedComplete={onHostedComplete}
            isPreview={isPreview}
            funnelId={funnelId}
            formFunnelSessionId={formFunnelSessionId}
            answers={answers}
            onProgressChange={onProgressChange}
          />
        );
      case "NO_MATCH":
        return (
          <NoMatchResult
            searchUrl={scenario.searchUrl}
            sessionId={sessionId}
            similarPrograms={scenario.similarPrograms}
          />
        );
      case "PROGRAM_UNAVAILABLE":
        return (
          <ProgramUnavailableResult
            programName={scenario.programName}
            searchUrl={scenario.searchUrl}
            sessionId={sessionId}
            similarPrograms={scenario.similarPrograms}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isPreview && <PreviewResultBadge />}
      {renderResult()}
    </>
  );
};
